import { ReactElement, ReactNode, Suspense, lazy, useCallback, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as ExpandLessIcon } from '../../../assets/icons/arrow-up-s-line.svg';
import { ReactComponent as SegmentIcon } from '../../../assets/icons/burger-compact.svg';
import { ReactComponent as MenuIcon } from '../../../assets/icons/burger.svg';
import { ReactComponent as DashboardLogo } from '../../../assets/svg/menu_dashboard.svg';
import { ReactComponent as MenuLogo } from '../../../assets/svg/menu_feedback.svg';
import { ReactComponent as LogoutLogo } from '../../../assets/svg/menu_logout.svg';
import { ReactComponent as PortfolioLogo } from '../../../assets/svg/menu_portfolio.svg';
import { ReactComponent as SettingsLogo } from '../../../assets/svg/menu_settings.svg';
import { ReactComponent as TradingLogo } from '../../../assets/svg/menu_trading.svg';
import { useDialog } from '../../../context/DialogContext';
import { useLocale } from '../../../context/NbLocalizationContext';
import { NbIconButton } from '../../../shared/components/icon-button/IconButton';
import { NbLoader } from '../../../shared/components/loader/Loader';
import { Config } from '../../../shared/config/config';
import { routes } from '../../../shared/data/routes';
import { useLogout } from '../../../shared/services/authorization/useLogout';
import { cssWhen } from '../../../shared/utils/themeUtils';
import classes from './Menu2.module.scss';

export enum MenuActionType {
  Logout = 'logout',
  Feedback = 'feedback',
}

export interface NbMenuItemProps {
  testId: string;
  text: string;
  link?: string;
  icon?: ReactElement;
  actionType?: MenuActionType;
  submenuList?: Array<NbSubmenuItemsProps>;
}

export interface NbSubmenuItemsProps {
  testId: string;
  text: string;
  link: string;
}

type OtherMenuItemProps = {
  testId: string;
  text: string;
  icon: ReactElement;
  actionType: MenuActionType;
};

export interface NbMenuItemsProps {
  overriddenMenuPoints?: NbMenuItemProps[];
  toggleMenuCollapse: () => void;
  isMenuCollapsed: boolean;
}

const defaultMenuPoints: Array<NbMenuItemProps> = [
  {
    testId: 'dashboard-menu-item',
    text: 'dashboard',
    link: routes.home,
    icon: <DashboardLogo />,
  },
  {
    testId: 'trading-menu-item',
    text: 'trading',
    icon: <TradingLogo />,
    submenuList: [
      {
        testId: 'trading-submenu-item-stock-trading',
        text: 'stockTrading',
        link: routes.trading,
      },
      {
        testId: 'trading-submenu-item-watch-list',
        text: 'watchList',
        link: routes.watchlist,
      },
      {
        testId: 'trading-submenu-item-chart',
        text: 'chart',
        link: routes.chart,
      },
    ],
  },
  {
    testId: 'portfolio-menu-item',
    text: 'portfolio',
    link: routes.portfolio,
    icon: <PortfolioLogo />,
  },
  {
    testId: 'settings-menu-item',
    text: 'settings',
    icon: <SettingsLogo />,
    submenuList: [
      {
        testId: 'settings-submenu-item-user-settings',
        text: 'userSettings',
        link: routes.userSettings,
      },
      {
        testId: 'settings-submenu-item-site-settings',
        text: 'siteSettings',
        link: routes.siteSettings,
      },
      {
        testId: 'settings-submenu-item-data-service-management',
        text: 'dataServiceManagement',
        link: routes.dataServiceManagement,
      },
    ],
  },
];

const otherMenuPoints: Array<OtherMenuItemProps> = Config.FeatureFlags.isUserFeedbackEnabled
  ? [
      {
        testId: 'feedback-menu-item',
        text: 'feedback',
        icon: <MenuLogo />,
        actionType: MenuActionType.Feedback,
      },
      {
        testId: 'logout-button',
        text: 'logout',
        icon: <LogoutLogo />,
        actionType: MenuActionType.Logout,
      },
    ]
  : [
      {
        testId: 'logout-button',
        text: 'logout',
        icon: <LogoutLogo />,
        actionType: MenuActionType.Logout,
      },
    ];

export const Menu2 = ({ overriddenMenuPoints, toggleMenuCollapse, isMenuCollapsed }: NbMenuItemsProps) => {
  const FeedbackModal = lazy(() => import('../../feedback/components/FeedbackModal'));
  const [menuState, setMenuState] = useState<Record<string, { isOpened: boolean }>>({});
  const [hoveredMenuState, setHoveredMenuState] = useState<string | undefined>();
  const { openDialog } = useDialog();
  const { translation } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useLogout();

  const menuPoints = useMemo(
    () => (overriddenMenuPoints ? overriddenMenuPoints : defaultMenuPoints),
    [overriddenMenuPoints],
  );

  const hideSubMenu = useCallback(
    (menuPoint: NbMenuItemProps) => {
      setHoveredMenuState(undefined);
      setMenuState(prevState => ({
        ...prevState,
        [menuPoint.text]: {
          isOpened: false,
        },
      }));
    },
    [setMenuState],
  );

  const delayHandler = useRef<NodeJS.Timer>(undefined);
  const handleMenuItemClick = (menuPoint: NbMenuItemProps, forceOpenClose?: boolean) => {
    clearTimeout(delayHandler.current as unknown as number);
    if (menuPoint.submenuList) {
      if (
        !forceOpenClose &&
        menuPoint.submenuList[0].link &&
        !location.pathname.startsWith(menuPoint.submenuList[0].link)
      ) {
        navigate(menuPoint.submenuList[0].link);
        hideSubMenu(menuPoint);
      } else {
        setMenuState(prevState => ({
          ...prevState,
          [menuPoint.text]: {
            isOpened: !prevState[menuPoint.text]?.isOpened,
          },
        }));
      }
    } else if (menuPoint.link) {
      navigate(menuPoint.link);
    } else if (menuPoint.actionType === MenuActionType.Logout) {
      logout();
    } else if (menuPoint.actionType === MenuActionType.Feedback) {
      openDialog<void>((close: () => void) => (
        <Suspense
          fallback={
            <div className={classes.feedbackLoader}>
              <NbLoader
                horizontalCenter
                verticalCenter
              />
            </div>
          }
        >
          <FeedbackModal onClose={close} />
        </Suspense>
      ));
    }
  };

  const handleMenuItemMouseEnter = (menuPoint: NbMenuItemProps) => {
    if (delayHandler.current !== undefined) {
      clearTimeout(delayHandler.current as unknown as number);
    }

    setHoveredMenuState(menuPoint.text);
  };

  const handleMenuItemMouseLeave = () => {
    delayHandler.current = setTimeout(() => {
      setHoveredMenuState(undefined);
    }, 400);
  };

  const getTriggerElement = (menuPoint: NbMenuItemProps, children: ReactNode) => {
    const className = cssWhen(
      classes.menuItem,
      [isMenuCollapsed, classes.menuItemIsCollapsed],
      // [!isMenuCollapsed && menuState[menuPoint.text]?.isOpened, classes.menuItemIsOpened],
      [menuPoint.link && location.pathname.startsWith(menuPoint.link), classes.menuItemIsActive],
      [menuPoint.text === 'logout', classes.menuItemIsLogout],
    );
    return !!menuPoint.link ? (
      <Link
        data-testid={menuPoint.testId}
        to={menuPoint.link}
        key={menuPoint.text}
        className={className}
      >
        {children}
      </Link>
    ) : (
      <button
        data-testid={menuPoint.testId}
        key={menuPoint.text}
        className={className}
        onClick={() => handleMenuItemClick(menuPoint)}
      >
        {children}
      </button>
    );
  };

  const isMenuPointActive = (menuPoint: NbMenuItemProps) => {
    return (
      (!!menuPoint.link && location.pathname.startsWith(menuPoint.link)) ||
      !!(menuPoint.submenuList && menuPoint.submenuList.some(isMenuPointActive))
    );
  };

  return (
    <>
      <div className={cssWhen(classes.menu, [isMenuCollapsed && !hoveredMenuState, classes.menuIsCollapsed])}>
        <div className={cssWhen(classes.toggleButton, [isMenuCollapsed, classes.toggleButtonIsCollapsed])}>
          <NbIconButton
            icon={isMenuCollapsed ? <SegmentIcon /> : <MenuIcon />}
            onClick={toggleMenuCollapse}
          />
        </div>

        <div className={cssWhen(classes.menuItems, [isMenuCollapsed, classes.menuItemsIsCollapsed])}>
          {menuPoints.map((menuPoint, ind) => (
            <div
              key={menuPoint.text}
              className={cssWhen(
                classes.menuItem,
                [isMenuCollapsed && menuState[menuPoint.text]?.isOpened, classes.menuItemIsCollapsedOpened],
                [!isMenuCollapsed && menuState[menuPoint.text]?.isOpened, classes.menuItemIsOpened],
                [isMenuPointActive(menuPoint), classes.menuItemIsActive],
                [
                  isMenuPointActive(menuPoint) && hoveredMenuState === menuPoint.text,
                  classes.menuItemIsActiveAndHovered,
                ],
                [menuPoint.text === 'logout', classes.menuItemIsLogout],
                [ind === 0, classes.menuItemIsFirst],
                [isMenuCollapsed && hoveredMenuState !== menuPoint.text, classes.menuItemIsCollapsed],
                [isMenuCollapsed && hoveredMenuState === menuPoint.text, classes.menuItemIsCollapsedAndHovered],
              )}
              onMouseEnter={() => handleMenuItemMouseEnter(menuPoint)}
              onMouseLeave={() => handleMenuItemMouseLeave()}
              onClick={e => {
                if (menuPoint.submenuList) {
                  handleMenuItemClick(menuPoint, true);
                  e.stopPropagation();
                }
              }}
            >
              {getTriggerElement(
                menuPoint,
                <>
                  <span
                    className={cssWhen(classes.menuItemIcon, [
                      hoveredMenuState === menuPoint.text && isMenuCollapsed,
                      classes.menuItemIconIsCollapsedHovered,
                    ])}
                  >
                    {menuPoint.icon}
                  </span>
                  <span className={classes.menuItemLabel}>
                    {translation.menu[menuPoint.text as keyof typeof translation.menu] || menuPoint.text}
                  </span>
                  {!!menuPoint.submenuList && (
                    <ExpandLessIcon
                      onClick={e => {
                        handleMenuItemClick(menuPoint, true);
                        e.stopPropagation();
                      }}
                      className={cssWhen(classes.menuItemSubListIcon, [
                        !menuState[menuPoint.text]?.isOpened,
                        classes.menuItemSubListIconIsOpened,
                      ])}
                    />
                  )}
                </>,
              )}

              {menuPoint.submenuList && menuState[menuPoint.text]?.isOpened && !isMenuCollapsed && (
                <div className={cssWhen(classes.subElementList)}>
                  {menuPoint.submenuList.map(submenuPoint => (
                    <div
                      key={submenuPoint.text}
                      className={cssWhen(classes.subElement, [
                        isMenuPointActive(submenuPoint),
                        classes.subElementIsActive,
                      ])}
                    >
                      <Link
                        data-testid={submenuPoint.testId}
                        to={submenuPoint.link}
                        onClick={() => hideSubMenu(menuPoint)}
                      >
                        {translation.menu[submenuPoint.text as keyof typeof translation.menu] || submenuPoint.text}
                      </Link>
                    </div>
                  ))}
                </div>
              )}

              {menuPoint.submenuList && hoveredMenuState === menuPoint.text && isMenuCollapsed && (
                <div
                  className={cssWhen(
                    classes.subElementList,
                    [isMenuCollapsed, classes.subElementListIsCollapsed],
                    [isMenuCollapsed && menuState[menuPoint.text]?.isOpened, classes.subElementListIsCollapsedOpened],
                    [!isMenuCollapsed && menuState[menuPoint.text]?.isOpened, classes.subElementListIsOpened],
                  )}
                >
                  {menuPoint.submenuList.map(submenuPoint => (
                    <div
                      key={submenuPoint.text}
                      className={cssWhen(
                        classes.subElement,
                        [isMenuPointActive(submenuPoint), classes.subElementIsActive],
                        [isMenuCollapsed, classes.subElementIsCollapsed],
                      )}
                    >
                      <Link
                        data-testid={submenuPoint.testId}
                        to={submenuPoint.link}
                        onClick={() => hideSubMenu(menuPoint)}
                      >
                        {translation.menu[submenuPoint.text as keyof typeof translation.menu] || submenuPoint.text}
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={classes.logoutSpacer} />

        {otherMenuPoints.map(menuPoint => (
          <div
            key={menuPoint.text}
            className={cssWhen(
              classes.menuItem,
              [isMenuCollapsed && menuState[menuPoint.text]?.isOpened, classes.menuItemIsCollapsedOpened],
              [!isMenuCollapsed && menuState[menuPoint.text]?.isOpened, classes.menuItemIsOpened],
              [isMenuCollapsed && hoveredMenuState === menuPoint.text, classes.menuItemIsCollapsedAndHovered],
              classes.menuItemIsLogout,
              [isMenuCollapsed && hoveredMenuState === menuPoint.text, classes.menuItemIsLogoutAndHovered],
              [menuPoint.text === 'feedback', classes.menuItemIsFeedback],
            )}
            onMouseEnter={() => handleMenuItemMouseEnter(menuPoint)}
            onMouseLeave={() => handleMenuItemMouseLeave()}
          >
            {getTriggerElement(
              menuPoint,
              <>
                <span className={classes.menuItemIcon}>{menuPoint.icon}</span>
                <span className={classes.menuItemLabel}>
                  {translation.menu[menuPoint.text as keyof typeof translation.menu] || menuPoint.text}
                </span>
              </>,
            )}
          </div>
        ))}
      </div>
    </>
  );
};
